import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { dealService } from '@/api/services/DealService';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import type { DealOrderType, YearSchedule } from '@/types/deals';

const DEALS_PAGE_SIZE = 20;

export const dealsKeys = {
  // scope — вкладка списка: 'SALE'/'PURCHASE' (продавец/покупатель) либо 'active'/'completed' (агент).
  list: (scope: string, page: number, filter: string) =>
    ['bargains', 'list', scope, page, filter] as const,
  steps: (dealId: number) => ['bargain', dealId, 'steps'] as const,
  documents: (dealId: number) => ['bargain', dealId, 'documents'] as const,
  absentDocuments: (userId: number, dealId: number) => ['bargain', dealId, 'absentDocuments', userId] as const,
  availableDocTypes: (dealId: number) => ['bargain', dealId, 'availableDocTypes'] as const,
};

/**
 * Список моих сделок с серверной пагинацией (page 1-based).
 * Продавец/покупатель — по вкладке `orderType` (продажи/покупки). Агент — по статусу `completed`
 * (активные/завершённые), без `orderType`: он посредник во всех сделках, деления по стороне нет.
 */
export function useMyBargains(params: {
  readonly orderType?: DealOrderType;
  readonly completed?: boolean;
  readonly page: number;
  readonly filter?: string;
}) {
  const { isAuthenticated } = useAuth();
  const { orderType, completed, page, filter = '' } = params;
  const scope = orderType ?? (completed ? 'completed' : 'active');
  return useQuery({
    queryKey: dealsKeys.list(scope, page, filter),
    queryFn: ({ signal }) =>
      dealService.getMyBargains(
        { orderType, completed, page, limit: DEALS_PAGE_SIZE, filter: filter.trim() || undefined },
        signal,
      ),
    enabled: isAuthenticated,
    // НЕ держим предыдущие данные между сменой вкладки/страницы: иначе placeholder с данными другой
    // вкладки (Продажи/Покупки) отрисуется с контрагентом по новой вкладке — неверно. Списки короткие,
    // показываем скелетон при загрузке новой страницы.
    staleTime: 30_000,
  });
}

/** Состояние одной сделки (степпер/шапка/документы). Источник истины UI. */
export function useBargainSteps(dealId: number | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: dealsKeys.steps(dealId ?? -1),
    queryFn: ({ signal }) => dealService.getBargainSteps(dealId!, signal),
    enabled: isAuthenticated && dealId != null && !Number.isNaN(dealId),
    // Сделка живёт асинхронно (Camunda/1С/ЭДО/экспедитор) → короче staleTime + рефетч при фокусе
    // (Фаза A0: обновление вручную; Фаза A1 добавит SSE-инвалидацию).
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** Дерево документов сделки. */
export function useDocumentsTree(dealId: number | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: dealsKeys.documents(dealId ?? -1),
    queryFn: ({ signal }) => dealService.getDocumentsTree(dealId!, signal),
    enabled: isAuthenticated && dealId != null && !Number.isNaN(dealId),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** Скачать документ сделки с дружелюбной ошибкой (502/503 → «сервис недоступен»). */
export function useDownloadDocument() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (documentId: number) => dealService.downloadDocument(documentId),
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('app.deals.docs.downloadError'),
      });
    },
  });
}

/** Ручное обновление сделки (кнопка «Обновить»): перечитать шаги + документы. */
export function useRefreshDeal(dealId: number | null) {
  const queryClient = useQueryClient();
  return () => {
    if (dealId == null) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: dealsKeys.steps(dealId) });
    void queryClient.invalidateQueries({ queryKey: dealsKeys.documents(dealId) });
  };
}

// ── Шаг «Аттестация» ──────────────────────────────────────────────────────────

/** Недостающие документы контрагента (userId — проверяемый, напр. продавец). */
export function useAbsentDocuments(userId: number | null | undefined, dealId: number | null) {
  const { isAuthenticated } = useAuth();
  const enabled = isAuthenticated && userId != null && dealId != null && !Number.isNaN(dealId);
  return useQuery({
    queryKey: dealsKeys.absentDocuments(userId ?? -1, dealId ?? -1),
    queryFn: ({ signal }) => dealService.getAbsentDocuments(userId!, dealId!, signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Скачать пакет аттестации (.zip) с дружелюбной ошибкой. */
export function useDownloadVerificationZip() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ dealId, isSeller }: { dealId: number; isSeller: boolean }) =>
      dealService.downloadVerificationZip(dealId, isSeller),
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('app.deals.attestation.zipError'),
      });
    },
  });
}

// Действие изменило состояние сделки → перечитать шаги/документы и списки сделок (статус мог поменяться).
function useDealActionInvalidate(dealId: number) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: dealsKeys.steps(dealId) });
    void queryClient.invalidateQueries({ queryKey: dealsKeys.documents(dealId) });
    void queryClient.invalidateQueries({ queryKey: ['bargains'] });
  };
}

function dealActionError(error: unknown, t: (k: string) => string): void {
  notifications.show({
    color: 'red',
    message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
  });
}

/** Аттестовать продавца (покупатель). Успех → следующий шаг. */
export function useAttest(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService.attest(dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.attestation.attested') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

/** Не аттестовать продавца (покупатель). Успех → сделка отменена. */
export function useNotAttest(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService.notAttest(dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'orange', message: t('app.deals.attestation.canceled') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Согласование дат отгрузки» ────────────────────────────────────────────

/** Отправить график на согласование (продавец или покупатель — по роли). */
export function useSuggestSchedule(dealId: number, role: 'seller' | 'buyer') {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: (dates: YearSchedule[]) =>
      role === 'seller' ? dealService.suggestSellerSchedule(dealId, dates) : dealService.suggestBuyerSchedule(dealId, dates),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.schedule.sent') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

/** Подтвердить предложенный график. */
export function useConfirmSchedule(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService.confirmSchedule(dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.schedule.confirmed') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Окончательный расчёт» ──────────────────────────────────────────────────

export type PaymentActionKind =
  | 'payUnderpayment'
  | 'confirmUnderpaymentReceived'
  | 'returnOverpayment'
  | 'confirmOverpaymentReceived';

/** Подтверждение шага окончательного расчёта (оплата/получение средств) — по виду действия. */
export function usePaymentConfirm(dealId: number, kind: PaymentActionKind) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService[kind](dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.finalPayment.confirmed') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Перевозка» ─────────────────────────────────────────────────────────────

/** Покупатель подтверждает получение товара. */
export function useConfirmDelivery(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService.confirmDelivery(dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.forwarding.confirmed') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

/** Загрузить подписанный дневной УПД (симуляция ЭДО на dev, где Диадок недоступен). */
export function useUploadSignedDailyUpd(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: (file: File) => dealService.uploadSignedDailyUpd(dealId, file),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.forwarding.updSigned') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Выбор грузополучателя» ─────────────────────────────────────────────────

/** Грузополучатель сделки по умолчанию. */
export function useBargainConsignee(dealId: number | null, enabled: boolean) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['bargain', dealId ?? -1, 'consignee'] as const,
    queryFn: ({ signal }) => dealService.getConsignee(dealId!, signal),
    enabled: isAuthenticated && enabled && dealId != null && !Number.isNaN(dealId),
    staleTime: 60_000,
  });
}

/** Назначить грузополучателя (запускает экспедирование). */
export function useAssignConsignee(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: (consigneeInn: string) => dealService.assignConsignee(dealId, consigneeInn),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.consignee.assigned') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Подтверждение отгрузки» ────────────────────────────────────────────────

/** Продавец подтверждает готовность товара к отгрузке. */
export function useConfirmShipmentReady(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: () => dealService.confirmShipmentReady(dealId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.shipmentReady.confirmed') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

// ── Шаг «Подпись спецификации» ──────────────────────────────────────────────────

/** Типы документов, доступные текущему пользователю для загрузки сейчас (бэк: роль + шаг). */
export function useAvailableDocTypes(dealId: number | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: dealsKeys.availableDocTypes(dealId ?? -1),
    queryFn: ({ signal }) => dealService.getAvailableDocTypes(dealId!, signal),
    enabled: isAuthenticated && dealId != null && !Number.isNaN(dealId),
    staleTime: 15_000,
  });
}

/** Загрузить подписанную вне ЭДО спецификацию (фолбэк/симуляция). Успех → подпись роли проставлена. */
export function useUploadSignedSpec(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: (file: File) => dealService.uploadSignedSpecification(dealId, file),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.spec.uploaded') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

/** Загрузить поручение экспедитору, подписанное агентом вне ЭДО. Успех → сделка переходит к ожиданию перевозчика. */
export function useUploadSignedForwarderAssignment(dealId: number) {
  const { t } = useTranslation();
  const invalidate = useDealActionInvalidate(dealId);
  return useMutation({
    mutationFn: (file: File) => dealService.uploadSignedForwarderAssignment(dealId, file),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: t('app.deals.forwarderAssignment.uploaded') });
    },
    onError: (error) => dealActionError(error, t),
  });
}

export { DEALS_PAGE_SIZE };
